import { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import FileUpload from 'components/FileUpload';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';
// PDF to JPG — renders each PDF page to a canvas, then exports as JPG.
// Runs fully in the browser using pdf.js. No server needed.
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function PdfToJpg() {
  const [file, setFile]   = useState(null);
  const [pages, setPages] = useState([]);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const onFileChange = f => {
    setError(''); setPages([]);
    if (!f) { setFile(null); return; }
    if (!f.type.includes('pdf')) { setError('Please select a PDF file.'); setFile(null); return; }
    if (f.size > 30 * 1024 * 1024) { setError('File too large. Max 30MB.'); setFile(null); return; }
    setFile(f);
  };

  const convert = async () => {
    if (!file) { setError('Please select a PDF file first.'); return; }
    setBusy(true); setError(''); setPages([]);
    try {
      const buf = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      const out = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        out.push({ url: canvas.toDataURL('image/jpeg', 0.92), num: i });
      }
      setPages(out);
    } catch (e) {
      setError('Could not convert this PDF. It may be corrupted or password-protected.');
    }
    setBusy(false);
  };

  const reset = () => { setFile(null); setPages([]); setError(''); };

  return (
    <>
    <Seo title="PDF to JPG Converter Free" description="Convert each PDF page into a JPG image. Free, runs in your browser." path="/tools/pdf/to-jpg" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="image" size={24} /></div>
            <div>
              <h1>PDF to JPG</h1>
              <p>Convert each page of a PDF into a JPG image. Runs in your browser — your files stay private.</p>
            </div>
          </div>
        </div>

        {pages.length === 0 ? (
          <div className="tool-box">
            <FileUpload
              accept="application/pdf"
              onFileChange={onFileChange}
              hint="Supported: PDF · Max 30MB"
              label="Click or drag & drop your PDF here"
            />
            {error && (
              <div className="alert alert-error" style={{ marginTop: 14 }}>
                <Icon name="alertCircle" size={16} />{error}
              </div>
            )}
            <button className="btn btn-primary btn-lg btn-full" style={{ marginTop: 20 }} onClick={convert} disabled={busy}>
              {busy ? <><span className="spinner sp-sm" />Converting pages...</> : <><Icon name="convert" size={18} />Convert to JPG</>}
            </button>
          </div>
        ) : (
          <div className="tool-box">
            <div className="alert alert-success" style={{ marginBottom: 16 }}>
              <Icon name="checkCircle" size={16} />Done! {pages.length} page{pages.length>1?'s':''} converted.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:14 }}>
              {pages.map(p => (
                <div key={p.num} style={{ border:'1px solid var(--slate-200)', borderRadius:'var(--r)', overflow:'hidden' }}>
                  <img src={p.url} alt={`Page ${p.num}`} style={{ width:'100%', display:'block' }} />
                  <a className="btn btn-secondary btn-sm btn-full" href={p.url} download={`page-${p.num}.jpg`} style={{ borderRadius:0 }}>
                    <Icon name="download" size={14} />Page {p.num}
                  </a>
                </div>
              ))}
            </div>
            <button className="btn btn-outline btn-lg" onClick={reset} style={{ marginTop:16 }}>
              <Icon name="refresh" size={18} />Convert Another PDF
            </button>
          </div>
        )}

        <div className="alert alert-info" style={{ marginTop: 14 }}>
          <Icon name="info" size={16} />
          <span>Each PDF page becomes a separate JPG you can download. Everything happens in your browser — your PDF is never uploaded.</span>
        </div>
         <ToolSeoContent tool="pdf-to-jpg" />
      </div>
    </div>
  </>
  );
}
